import { computed, nextTick, ref, shallowRef, toValue, watch } from "vue";
import type { MaybeRefOrGetter } from "vue";
import DxScrollView from "devextreme-vue/scroll-view";
import type { DxScrollViewTypes } from "devextreme-vue/scroll-view";

export interface InfiniteScrollPageResult<TItem> {
  items: TItem[];
  hasMore: boolean;
  totalItems: number;
}

export interface InfiniteScrollFetchContext<TFiltros> {
  skip: number;
  take: number;
  filtros: TFiltros;
  append: boolean;
}

export interface InfiniteScrollUnpagedResult<TItem> {
  items: TItem[];
  totalItems: number;
}

export interface UseInfiniteScrollPagedOptions<TItem, TFiltros> {
  pageSize?: number;
  fetchPage: (ctx: InfiniteScrollFetchContext<TFiltros>) => Promise<InfiniteScrollPageResult<TItem>>;
  fetchUnpaged?: (filtros: TFiltros) => Promise<InfiniteScrollUnpagedResult<TItem>>;
  isActive?: MaybeRefOrGetter<boolean>;
}

/**
 * Paginación al scroll: @reach-bottom llama a fetchPage.
 */
export function useInfiniteScrollPaged<TItem, TFiltros>(
  filtrosSource: MaybeRefOrGetter<TFiltros | null | undefined>,
  options: UseInfiniteScrollPagedOptions<TItem, TFiltros>,
) {
  const pageSize = options.pageSize ?? 15;

  const scrollViewRef = ref<InstanceType<typeof DxScrollView> | null>(null);
  const items = shallowRef<TItem[]>([]);
  const skip = ref(0);
  const hasMore = ref(true);
  const loading = ref(false);
  const loadError = ref(false);
  const fetchVersion = ref(0);
  const totalItems = ref(0);
  /** Si true, el próximo cambio de filtros no dispara `loadNextPage` (p. ej. porque vendrá `loadFullListUnpaged`). */
  const skipNextWatchPagedReload = ref(false);

  const active = computed(() =>
    options.isActive !== undefined ? toValue(options.isActive) : toValue(filtrosSource) != null,
  );

  const reachBottomEnabled = computed(() => active.value && hasMore.value);
  const showScrollbar = computed<DxScrollViewTypes.Properties["showScrollbar"]>(() => "onScroll");

  /** Invalida peticiones en curso y deja la lista lista para la primera página. Devuelve el nuevo fetchVersion. */
  function startFreshQuery(): number {
    fetchVersion.value++;
    const version = fetchVersion.value;
    items.value = [];
    skip.value = 0;
    hasMore.value = true;
    totalItems.value = 0;
    loadError.value = false;
    loading.value = false;
    return version;
  }

  /**
   * Llamar antes de actualizar filtros cuando a continuación se hará `loadFullListUnpaged`,
   * para evitar la petición paginada redundante del watch.
   */
  function skipNextFiltrosReloadForFullList(): void {
    skipNextWatchPagedReload.value = true;
  }

  watch(
    () => toValue(filtrosSource),
    async (f) => {
      const version = startFreshQuery();
      if (f != null) {
        if (skipNextWatchPagedReload.value) {
          skipNextWatchPagedReload.value = false;
          return;
        }
        await loadNextPage(version, false, f);
      }
    },
    { immediate: true },
  );

  async function onReachBottom(e: DxScrollViewTypes.ReachBottomEvent): Promise<void> {
    try {
      const f = toValue(filtrosSource);
      if (f == null || loading.value || !hasMore.value) {
        return;
      }
      await loadNextPage(fetchVersion.value, true, f);
    } finally {
      await nextTick();
      e.component.release(false);
    }
  }

  async function loadNextPage(version: number, append: boolean, filtros: TFiltros): Promise<void> {
    if (loading.value) {
      return;
    }

    loading.value = true;
    loadError.value = false;
    const currentSkip = append ? skip.value : 0;

    try {
      const { items: batch, hasMore: more, totalItems: total } = await options.fetchPage({
        skip: currentSkip,
        take: pageSize,
        filtros,
        append,
      });

      if (version !== fetchVersion.value) {
        return;
      }

      items.value = append ? items.value.concat(batch) : batch;
      skip.value = currentSkip + batch.length;
      hasMore.value = more;
      totalItems.value = total;
    } catch {
      if (version === fetchVersion.value) {
        loadError.value = true;
      }
    } finally {
      if (version === fetchVersion.value) {
        loading.value = false;
      }
    }
  }

  async function loadFullListUnpaged(): Promise<void> {
    if (options.fetchUnpaged == null) {
      return;
    }
    const f = toValue(filtrosSource);
    if (f == null) {
      return;
    }
    const version = startFreshQuery();
    loading.value = true;
    loadError.value = false;
    try {
      const { items: batch, totalItems: total } = await options.fetchUnpaged(f);
      if (version !== fetchVersion.value) {
        return;
      }
      items.value = batch;
      skip.value = batch.length;
      hasMore.value = false;
      totalItems.value = total;
    } catch {
      if (version === fetchVersion.value) {
        loadError.value = true;
      }
    } finally {
      if (version === fetchVersion.value) {
        loading.value = false;
      }
    }
  }

  return {
    scrollViewRef,
    items,
    totalItems,
    loading,
    loadError,
    hasMore,
    reachBottomEnabled,
    showScrollbar,
    onReachBottom,
    loadFullListUnpaged,
    skipNextFiltrosReloadForFullList,
  };
}
